import "./assets/tokens/index.css";
import "./assets/scss/bootstrap.scss";
import "./assets/styles/portal.css";
import "bootstrap/js/dist/modal";
import "bootstrap/js/dist/toast";

import { createApp } from 'vue';
import { router, homeRoute } from './router';
import BsTooltip from 'bootstrap/js/dist/tooltip';

import store from './store';
import defaultAvatar from "/assets/images/avatar.png";

import { 
  initializeApp 
} from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { 
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  serverTimestamp, 
  FieldValue, 
  Timestamp, 
  FieldPath, 
  documentId,
  collection, 
  collectionGroup,
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  addDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  writeBatch,
  onSnapshot,
  getCountFromServer
} from 'firebase/firestore';

// FontAwesome: each component imports and registers only the icons it uses
// (see `data() { return { faX, ... } }` + `<fa :icon="faX" />` in components),
// so no central icon registry is needed here.
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import App from './App.vue';

// Lazy components (e.g. admin blocks on Settings) fail silently when a deploy removed their old chunk; reload once to get fresh assets.
window.addEventListener('vite:preloadError', (event) => {
  const lastReload = Number(sessionStorage.getItem('preload-error-reload') || 0);
  if (Date.now() - lastReload < 30000) return;
  event.preventDefault();
  sessionStorage.setItem('preload-error-reload', String(Date.now()));
  window.location.reload();
});

// Initialize Firebase
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const firebaseApp = initializeApp(firebaseConfig);

const db = initializeFirestore(firebaseApp, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});
const auth = getAuth(firebaseApp);

// storage, functions and httpsCallable come from firebaseServices.js
export {
  firebaseApp,
  auth,
  db,
  // Export modular helpers for use in components
  serverTimestamp,
  FieldValue,
  Timestamp,
  FieldPath,
  documentId,
  EmailAuthProvider,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  onAuthStateChanged,
  onSnapshot
};

// TEMP: startup timing, measured from the start of the page load
const bootLog = (label) => console.info(`[Opstarten] ${label}: ${Math.round(performance.now())} ms`);
bootLog('main.js geladen');

//Method to get user data from firestore; also used after accepting an invitation, without a page reload
export async function getUserData() {
  const userDocRef = doc(db, "users", auth.currentUser.uid);

  //Get user data to put in store
  try {
    // The schoolId claim lets the school load alongside the user doc instead of after it
    const tokenPromise = auth.currentUser.getIdTokenResult();
    tokenPromise.then(() => bootLog('token'));
    const claimSchoolPromise = tokenPromise
      .then((token) => token?.claims?.schoolId ? getDoc(doc(db, "schools", token.claims.schoolId)) : null)
      .then((snap) => { bootLog('school (via claim)'); return snap; })
      .catch((error) => { bootLog('school (via claim) mislukt: ' + (error.code || error.message)); return null; });
    const userDocPromise = getDoc(userDocRef);
    userDocPromise.then((snap) => bootLog('gebruiker (fromCache=' + snap.metadata.fromCache + ')'));
    const [userDocSnap, firstToken] = await Promise.all([userDocPromise, tokenPromise]);
    const userData = userDocSnap.data() || {};
    // After the read, not awaited: with an empty Firestore cache this pending write made getDoc return only { lastLogin }
    setDoc(userDocRef, { lastLogin: serverTimestamp() }, { merge: true })
      .catch((error) => console.log("Error updating last login: ", error));

    let token = firstToken;
    // A right granted or taken away while signed in only reaches the Firestore rules with a fresh token
    if (Boolean(token?.claims?.contentCreator) !== (userData.contentCreator === true)) {
      bootLog('token wordt ververst (contentCreator wijkt af)');
      token = await auth.currentUser.getIdTokenResult(true);
      bootLog('token ververst');
    }
    const isAdmin = Boolean(token?.claims?.admin);
    const isDeveloper = Boolean(token?.claims?.developer);

    if (isAdmin) {
      console.log("Custom claim admin found");
    }

    if (isDeveloper) {
      console.log("Custom claim developer found");
    }

    await store.dispatch('applyAuthenticatedUser', {
      userData,
      authUid: auth.currentUser.uid,
      isAdmin,
      isDeveloper,
      defaultAvatar
    });

    // Hydrate the school feature flags once, up front, so every screen can read
    // them from the store (selfStart, autoSettings, purchaseGames, ...).
    // The user doc decides the school; a claim that is missing or out of date falls back to a separate read
    if (userData.schoolId) {
      try {
        const claimSchoolSnap = await claimSchoolPromise;
        const schoolSnap = claimSchoolSnap?.id === userData.schoolId
          ? claimSchoolSnap
          : await getDoc(doc(db, "schools", userData.schoolId));
        bootLog(schoolSnap === claimSchoolSnap ? 'school uit claim gebruikt' : 'school los opgehaald');
        if (schoolSnap.exists()) {
          await store.dispatch('applySchoolSettings', schoolSnap.data());
        }
      } catch (schoolError) {
        console.log("Error getting school settings: ", schoolError);
      }
    }
  }
  catch (error) {
    console.log("Error getting user data: ", error);
  }
}

//Handle user login redirects to login page
onAuthStateChanged(auth, async (loggedUser) => {
  console.log('Login state change');
  bootLog('inlogstatus bekend');

  if (loggedUser != null) {  
      console.log(loggedUser.email + ' logged in');
      store.commit('SET_LOGGED_USER', loggedUser);
      await Promise.all([getUserData()]);
      bootLog('gebruikersgegevens klaar');

      mountApp();
      bootLog('app gemount');
      router.isReady().then(() => bootLog('eerste pagina geopend: ' + router.currentRoute.value.path));

      // Login via login form
      if (store.state.freshLogin) {
        console.log('Login via email');
        if(store.state.loginRedirect != null && store.state.loginRedirect != "" && store.state.loginRedirect != "/")
        {
          console.log('Redirect after login: ' + store.state.loginRedirect);
          router.replace(store.state.loginRedirect);
          store.commit('SET_LOGIN_REDIRECT', "");
          return;
        }
      }
      
      //Window location pathname is emtpy or coming from login page
      console.log("Window location pathname is emtpy or coming from login page : " + window.location.pathname);
      if(window.location.pathname == '/' || window.location.pathname == '/login' || window.location.pathname == ''){
        router.replace(homeRoute());
        return;
      }

      //Default route will be the one we requested
      return;
  } 

  // No user logged in
  console.log("window.location.pathname " + window.location.pathname);
  if(!window.location.pathname.includes('/invitation')){ 
    if(window.location.pathname != '/login')
      store.commit('SET_LOGIN_REDIRECT', window.location.pathname + window.location.hash);
    
    router.replace('/login');
  } 

  mountApp();
});

function mountApp(){
  //Mount app after we know if we have a user
  if(app != null) return;

  app = createApp(App)
    .component('fa', FontAwesomeIcon)
    .directive('tooltip', {
      mounted(el, binding) {
        // Initialize Bootstrap tooltip
        const options = typeof binding.value === 'object' ? binding.value : { title: binding.value };
        if (binding.arg) {
          options.placement = binding.arg; // e.g., v-tooltip:top
        }
        // Add default delay if not specified
        if (!options.delay) {
          options.delay = { show: 500, hide: 0 };
        }
        // Hover only: with focus a clicked button kept its tooltip, e.g. under an overlay it opened
        if (!options.trigger) {
          options.trigger = 'hover';
        }
        el._tooltip = new BsTooltip(el, options);
        el._hideTooltip = () => el._tooltip?.hide();
        el.addEventListener('click', el._hideTooltip);
      },
      updated(el, binding) {
        // Update tooltip title if binding value changes
        if (el._tooltip && binding.value !== binding.oldValue) {
          const newTitle = typeof binding.value === 'object' ? binding.value.title : binding.value;
          el._tooltip.setContent({ '.tooltip-inner': newTitle });
        }
      },
      unmounted(el) {
        // Clean up tooltip when element is removed
        if (el._tooltip) {
          el._tooltip.dispose();
          delete el._tooltip;
        }
        el.removeEventListener('click', el._hideTooltip);
      }
    })
    .use(store)
    .use(router);

  //Remove main-loading div
  let mainLoading = document.getElementById('main-loading');
  if(mainLoading != null){
    mainLoading.remove();
  }

  app.mount('#yuha-app');
}

router.beforeEach((to, from) => {
  store.commit('SET_SHOW_MENU', false);
  console.log(from.path + " -> " + to.path);
  if(to.path !== '/login' && to.path.indexOf('/invitation') < 0){
      if(auth.currentUser == null){
        return '/login';
      }
    }

  return true;
});

let app;